/**
 * Check if conditionals are satisfied based on current game state
 * @param {Array} conditionals - Array of conditional groups
 * @param {Array} flags - Current flags state
 * @param {Array} scales - Current scales state
 * @param {Array} completedEndingIds - Array of completed ending IDs
 * @returns {boolean} - True if conditionals are satisfied
 */
export const checkConditionals = (conditionals, flags, scales, completedEndingIds) => {
  if (!conditionals || conditionals.length === 0) {
    return true;
  }
  
  // OR groups logic - at least one conditional group must be satisfied
  for (const conditional of conditionals) {
    let groupSatisfied = true;
    
    // Check all flag conditions (AND logic within the group)
    if (conditional.flags) {
      for (const flagCondition of conditional.flags) {
        const flag = flags.find(f => f.id === flagCondition.flag_id);
        if (!flag || flag.value !== flagCondition.has) {
          groupSatisfied = false;
          break;
        }
      }
    }
    
    // If flags failed, skip to next conditional
    if (!groupSatisfied) {
      continue;
    }
    
    // Check all scale conditions (AND logic within the group)
    if (conditional.scales) {
      for (const scaleCondition of conditional.scales) {
        const scale = scales.find(s => s.id === scaleCondition.scale_id);
        if (!scale) {
          groupSatisfied = false;
          break;
        }
        
        // Check scale conditions based on threshold type (case-insensitive)
        const typeUpper = scaleCondition.type?.toUpperCase();
        if (typeUpper === 'EQ' && scale.value !== scaleCondition.threshold) {
          groupSatisfied = false;
          break;
        } else if (typeUpper === 'GTE' && scale.value < scaleCondition.threshold) {
          groupSatisfied = false;
          break;
        } else if (typeUpper === 'LTE' && scale.value > scaleCondition.threshold) {
          groupSatisfied = false;
          break;
        }
      }
    }
    
    // If scales failed, skip to next conditional
    if (!groupSatisfied) {
      continue;
    }
    
    // Check all completed endings conditions (AND logic within the group).
    // Each entry is { ending_id, has }: has=true requires the ending be completed,
    // has=false requires it NOT be completed. Mirrors backend check_conditionals.
    if (conditional.completed_endings) {
      const completedSet = completedEndingIds.map(String);
      for (const endingCondition of conditional.completed_endings) {
        const inSet = completedSet.includes(String(endingCondition.ending_id));
        if (endingCondition.has && !inSet) {
          groupSatisfied = false;
          break;
        }
        if (!endingCondition.has && inSet) {
          groupSatisfied = false;
          break;
        }
      }
    }
    
    // If this conditional group is satisfied, return True
    if (groupSatisfied) {
      return true;
    }
  }
  
  // No conditional group was satisfied
  return false;
};
